import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type Ref } from 'react';
import { cn } from '../lib/utils';

// Figma "Badge" (design system `457:3312`) — solid colour families, matching
// Button's own vocabulary (primary/secondary/outline/destructive) plus the status
// colours and the on-media `video` treatment. Only default/idle styling — Figma
// also defines hover/disabled states per type, deliberately left out here since
// any interactive use (see `video`) composes its own hover/click at the call site
// rather than baking an "is this clickable" axis into the primitive.
const badgeVariants = cva(
  // No base `border` here — Figma's spec (457:3312) gives filled variants zero border, only
  // `outline` has one. Height is locked by `size` regardless of border, so a base
  // `border-transparent` doesn't grow the badge — it just eats 2px of content height out of
  // every variant's fixed box, clipping `label-sm` text in `compact` and 16px icons in
  // `default`. Keeping the border scoped to `outline` restores Figma's real content budget.
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-[4px] label-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        primary:
          'bg-interaction-primary-background-default text-interaction-primary-content-default',
        secondary:
          'bg-interaction-secondary-background-default text-interaction-secondary-content-default',
        outline: 'border border-border-muted text-interaction-neutral-content-default',
        destructive:
          'bg-interaction-destructive-background-default text-interaction-destructive-content-default',
        success: 'bg-status-green text-white',
        warning: 'bg-status-yellow text-white',
        info: 'bg-status-blue text-white',
        // On-media only — literal translucent black, matching Figma's own `type=video`
        // value exactly. Deliberately not the `interaction-video-*` tokens, which are
        // solid and belong to video-player chrome, not badges laid over a thumbnail.
        video: 'bg-black/80 text-white',
      },
      size: {
        default: 'min-h-6 max-h-6 px-2 py-1',
        compact: "min-h-5 max-h-5 px-2 py-1 [&_svg:not([class*='size-'])]:size-3.5",
      },
    },
    defaultVariants: {
      variant: 'outline',
      size: 'default',
    },
  },
);

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;
export type BadgeSize = NonNullable<VariantProps<typeof badgeVariants>['size']>;

type BadgeProps = ComponentPropsWithoutRef<'span'> & {
  variant?: BadgeVariant;
  size?: BadgeSize;
  asChild?: boolean;
};

export const Badge = forwardRef<HTMLSpanElement | HTMLElement, BadgeProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    // Rendered directly so `ref` actually reaches the DOM node — required for
    // `asChild` composition under Popover/Tooltip triggers, which anchor their
    // positioning to that ref. Without this, e.g.
    // `<PopoverTrigger asChild><Badge asChild variant="video"><button/></Badge></PopoverTrigger>`
    // silently fails to open, since Radix never gets a node to attach to.
    if (asChild) {
      return (
        <Slot.Root
          ref={ref as Ref<HTMLElement>}
          data-slot="badge"
          className={cn(badgeVariants({ variant, size }), className)}
          {...props}
        />
      );
    }

    return (
      <span
        ref={ref as Ref<HTMLSpanElement>}
        data-slot="badge"
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Badge.displayName = 'Badge';
